import { CommandType, type ActivityEvent } from '@findmy/shared';

const commandLabel: Record<CommandType, string> = {
  ring: 'Ring',
  locate_now: 'Locate now',
  lost_mode_on: 'Lost mode',
  lost_mode_off: 'Lost mode off',
};

const retentionLabel = (days: string) => (days === '365' ? '1 year' : `${days} days`);

function settingsChanges(detail: Record<string, string>): string {
  const changes: string[] = [];
  if (detail.retentionDays) changes.push(`history kept for ${retentionLabel(detail.retentionDays)}`);
  if (detail.reverseGeocodeEnabled) {
    changes.push(detail.reverseGeocodeEnabled === 'true' ? 'addresses on' : 'addresses off');
  }
  if (detail.defaultLostContact) changes.push('lost-mode contact changed');
  return changes.length > 0 ? `Settings changed: ${changes.join(', ')}` : 'Settings changed';
}

/** One line for Settings → Security activity. */
export function describeActivity(event: ActivityEvent): string {
  const { detail } = event;
  const device = detail.name ? `“${detail.name}”` : 'a device';
  switch (event.type) {
    case 'register':
      return 'Account created';
    case 'login_ok':
      return detail.secondFactor === 'recovery' ? 'Logged in with a recovery code' : 'Logged in';
    case 'login_failed':
      return detail.step === 'code' ? 'Wrong two-factor code at login' : 'Failed login attempt';
    case 'logout':
      return 'Logged out';
    case 'password_reset':
      return 'Password reset on the server';
    case 'password_changed':
      return 'Password changed (other sessions logged out)';
    case 'two_factor_enabled':
      return 'Two-factor login turned on (other sessions logged out)';
    case 'two_factor_disabled':
      return 'Two-factor login turned off';
    case 'recovery_codes_created':
      return 'New recovery codes made (the old ones stopped working)';
    case 'pairing_code_created':
      return 'Pairing code created';
    case 'device_paired':
      return `Paired ${device}`;
    case 'device_renamed':
      return `Renamed a device to ${device}`;
    case 'device_revoked':
      return `Removed ${device} (history kept)`;
    case 'device_deleted':
      return `Deleted ${device} and its history`;
    case 'device_unpaired_self':
      return `${detail.name ? device : 'A device'} unpaired itself`;
    case 'command_sent': {
      const command = CommandType.safeParse(detail.command);
      const label = command.success ? commandLabel[command.data] : 'A command';
      return `${label} sent to ${device}`;
    }
    case 'settings_changed':
      return settingsChanges(detail);
    case 'export': {
      const format = (detail.format ?? 'json').toUpperCase();
      return detail.name ? `Exported ${device} as ${format}` : `Exported all data as ${format}`;
    }
    case 'history_deleted': {
      const n = Number(detail.fixes ?? 0);
      return `Deleted all location history (${n.toLocaleString('en-GB')} ${n === 1 ? 'location' : 'locations'})`;
    }
    default:
      return event.type.replace(/_/g, ' ');
  }
}

/** Events worth drawing the eye to. */
export const isWarning = (event: ActivityEvent) => event.type === 'login_failed';
